import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Send/receive HttpOnly auth cookie on every request
axios.defaults.withCredentials = true;

// Short-lived in-memory token cache for legacy raw-fetch callers.
// On page refresh the cookie is the source of truth — /auth/me re-validates.
let inMemoryToken = null;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshingRef = useRef(false);

  const api = useCallback(() => {
    const instance = axios.create({
      baseURL: API,
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      timeout: 30000,
    });

    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        // Only treat a 401 as a logout signal when it comes from an auth-critical
        // endpoint (the cookie itself is being rejected). Benign 401s from feature
        // endpoints (e.g. notification dismiss, livekit token, etc.) shouldn't
        // race-kick the user back to /login while they're navigating.
        if (error.response?.status === 401) {
          const url = error.config?.url || '';
          const isAuthEndpoint = url.includes('/auth/me') || url.includes('/auth/refresh') || url.endsWith('/auth/login');
          if (isAuthEndpoint) {
            inMemoryToken = null;
            setToken(null);
            setUser(null);
          }
        }
        return Promise.reject(error);
      }
    );

    return instance;
  }, [token]);

  // On mount: ask backend who we are using the HttpOnly cookie.
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(res.data);
      } catch {
        inMemoryToken = null;
        setToken(null);
        setUser(null);
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  // Refresh the current user object from the server. Callers hit this to pick
  // up changes like premium status, updated profile fields, etc. Guarded so
  // we don't stampede the server if many components mount at once.
  const refreshUser = useCallback(async () => {
    if (refreshingRef.current) return null;
    refreshingRef.current = true;
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
      return res.data;
    } catch {
      return null;
    } finally {
      refreshingRef.current = false;
    }
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    const { access_token, user: userData } = res.data;
    inMemoryToken = access_token;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password) => {
    const res = await axios.post(
      `${API}/auth/register`,
      { name, email, password },
      { withCredentials: true }
    );
    const { access_token, user: userData } = res.data;
    inMemoryToken = access_token;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // even if the call fails, clear local state
    }
    inMemoryToken = null;
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (data) => {
    const res = await api().put('/auth/profile', data);
    setUser(res.data);
    return res.data;
  };

  // For legacy raw fetch callers that want the token — they should also pass credentials:'include'.
  const getToken = useCallback(() => inMemoryToken, []);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      logout,
      updateProfile,
      api,
      getToken,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
